import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  StickyNote, Plus, Pencil, Trash2, Eye, Users, CheckCheck, Loader2, ThumbsUp,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";

const PRIORITY = {
  standard: { label: "Standard", dot: "#eab308", bg: "bg-amber-50", border: "border-amber-300", ring: "ring-amber-200", chip: "bg-amber-400" },
  important: { label: "Important", dot: "#f97316", bg: "bg-orange-50", border: "border-orange-300", ring: "ring-orange-200", chip: "bg-orange-500" },
  urgent: { label: "Urgent", dot: "#ef4444", bg: "bg-red-50", border: "border-red-400", ring: "ring-red-200", chip: "bg-red-500" },
};
const ORDER = ["standard", "important", "urgent"];
const emptyForm = { id: null, date: "", text: "", priority: "standard", audience: "all", roles: [], users: [], repeat: false, until: "" };
const fmtTime = (iso) => { try { return new Date(iso).toLocaleString("fr-BE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export default function AgendaPostits({ dates, dayLabels, monthLabels, highlightId }) {
  const { t } = useTranslation();
  const [postits, setPostits] = useState([]);
  const [recipients, setRecipients] = useState({ people: [], roles: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const frm = dates[0], to = dates[6];

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/planning/postits", { params: { frm, to } });
      setPostits(data.postits || []);
    } catch (e) { /* silencieux */ }
  }, [frm, to]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/planning/postits/recipients").then(({ data }) => setRecipients(data)).catch(() => {});
  }, []);

  const openAdd = (date) => { setForm({ ...emptyForm, date }); setOpen(true); };
  const openEdit = (p) => {
    setForm({ id: p.id, date: p.date, text: p.text, priority: p.priority, audience: p.audience, roles: p.roles || [], users: p.users || [] });
    setOpen(true);
  };

  const save = async () => {
    if (!form.text.trim()) { toast.error(t("postit.emptyMsg")); return; }
    if (form.audience === "roles" && !form.roles.length) { toast.error(t("postit.chooseRole")); return; }
    if (form.audience === "users" && !form.users.length) { toast.error(t("postit.choosePerson")); return; }
    setSaving(true);
    const payload = { date: form.date, text: form.text.trim(), priority: form.priority, audience: form.audience, roles: form.roles, users: form.users };
    if (!form.id && form.repeat && form.until && form.until >= form.date) {
      const list = []; const d = new Date(form.date + "T00:00:00"); const end = new Date(form.until + "T00:00:00");
      while (d <= end && list.length < 60) { list.push(d.toISOString().slice(0, 10)); d.setDate(d.getDate() + 1); }
      payload.dates = list;
    }
    try {
      if (form.id) { await api.put(`/planning/postits/${form.id}`, payload); toast.success(t("postit.updated")); }
      else { await api.post("/planning/postits", payload); toast.success(t("postit.published")); }
      setOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setSaving(false);
  };

  const markRead = async (p) => {
    try { await api.post(`/planning/postits/${p.id}/read`); await load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const toggleAck = async (p) => {
    try { await api.post(`/planning/postits/${p.id}/ack`); await load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const remove = async (p) => {
    let scope = "one";
    if (p.series_id) {
      const all = window.confirm("Ce post-it est récurrent (plusieurs jours).\n\nOK = supprimer TOUTE la série\nAnnuler = supprimer uniquement ce jour");
      scope = all ? "series" : "one";
    } else if (!window.confirm("Supprimer ce post-it ?")) return;
    try {
      const { data } = await api.delete(`/planning/postits/${p.id}?scope=${scope}`);
      toast.success(scope === "series" ? `Série supprimée (${data.deleted} jours).` : "Post-it supprimé.");
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const toggle = (field, val) => setForm((f) => ({ ...f, [field]: f[field].includes(val) ? f[field].filter((x) => x !== val) : [...f[field], val] }));

  return (
    <div className="mt-4" data-testid="agenda-postits">
      <div className="flex items-center gap-2 mb-2 px-1">
        <StickyNote className="h-4 w-4 text-amber-500" />
        <h3 className="text-sm font-bold text-slate-700">{t("postit.title")}</h3>
        <span className="text-xs text-slate-400">{t("postit.subtitle")}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
        {dates.map((date, di) => {
          const dayPostits = postits.filter((p) => p.date === date);
          const dt = new Date(date + "T00:00:00");
          return (
            <div key={date} className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-2 min-h-[70px]" data-testid={`postit-col-${date}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{dayLabels[di]?.slice(0, 3)} {dt.getDate()}</span>
                <button onClick={() => openAdd(date)} data-testid={`postit-add-${date}`} title={t("postit.addTitle")}
                  className="h-5 w-5 rounded-md bg-amber-400 hover:bg-amber-500 text-white flex items-center justify-center transition"><Plus className="h-3 w-3" /></button>
              </div>
              <div className="space-y-1.5">
                {dayPostits.map((p) => {
                  const meta = PRIORITY[p.priority] || PRIORITY.standard;
                  const hl = highlightId && p.id === highlightId;
                  return (
                    <motion.div key={p.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                      className={`rounded-lg border-l-4 ${meta.border} ${meta.bg} p-2 shadow-sm ${hl ? `ring-2 ${meta.ring}` : ""}`}
                      data-testid={`postit-card-${p.id}`}>
                      <div className="flex items-start justify-between gap-1">
                        <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase" style={{ color: meta.dot }}>
                          <span className="h-2 w-2 rounded-full" style={{ background: meta.dot }} />{t(`postit.priority.${p.priority}`)}
                        </span>
                        {p.can_manage && (
                          <div className="flex gap-1">
                            <button onClick={() => openEdit(p)} data-testid={`postit-edit-${p.id}`} className="text-slate-400 hover:text-blue-600"><Pencil className="h-3 w-3" /></button>
                            <button onClick={() => remove(p)} data-testid={`postit-del-${p.id}`} className="text-slate-400 hover:text-red-500"><Trash2 className="h-3 w-3" /></button>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-slate-800 leading-snug mt-1 whitespace-pre-wrap break-words">{p.text}</p>
                      <div className="text-[9px] text-slate-500 mt-1 truncate">{p.author_name}{p.audience !== "all" ? ` · ${p.audience_label}` : ""}</div>
                      <div className="flex items-center justify-between mt-1.5 gap-1">
                        <Popover>
                          <PopoverTrigger asChild>
                            <button data-testid={`postit-readers-${p.id}`} className="inline-flex items-center gap-1 text-[9px] font-semibold text-slate-500 hover:text-slate-800">
                              <Eye className="h-3 w-3" />{t("postit.readBy", { read: p.read_count, total: p.recipient_count })}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 p-3" align="start">
                            <div className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-1"><Users className="h-3.5 w-3.5" />{t("postit.readReceipts")}</div>
                            {p.read_by.length === 0 ? <p className="text-xs text-slate-400">{t("postit.noneRead")}</p> : (
                              <div className="space-y-1 max-h-40 overflow-y-auto">
                                {p.read_by.map((r) => (
                                  <div key={r.username} className="flex items-center justify-between text-xs">
                                    <span className="text-slate-700">{r.name}</span>
                                    <span className="text-slate-400 text-[10px]">{fmtTime(r.read_at)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="mt-2 pt-2 border-t text-[10px] text-slate-400">Publié le {fmtTime(p.created_at)}</div>
                          </PopoverContent>
                        </Popover>
                        {p.is_recipient && !p.has_read && (
                          <button onClick={() => markRead(p)} data-testid={`postit-read-${p.id}`}
                            className="inline-flex items-center gap-1 text-[9px] font-bold text-teal-700 bg-teal-100 hover:bg-teal-200 rounded px-1.5 py-0.5 transition">
                            <CheckCheck className="h-3 w-3" />{t("postit.markRead")}
                          </button>
                        )}
                        {p.has_read && !p.is_author && <span className="text-[9px] text-teal-600 font-semibold inline-flex items-center gap-0.5"><CheckCheck className="h-3 w-3" />{t("postit.read")}</span>}
                      </div>
                      {((p.is_recipient && !p.is_author) || p.ack_count > 0) && (
                        <div className="flex items-center justify-between mt-1.5">
                          {p.is_recipient && !p.is_author ? (
                            <button onClick={() => toggleAck(p)} data-testid={`postit-ack-${p.id}`}
                              className={`inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2 py-1 transition ${p.has_acked ? "bg-emerald-500 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}>
                              <ThumbsUp className="h-3 w-3" />{p.has_acked ? t("postit.ackDone") : t("postit.ackDo")}
                            </button>
                          ) : <span />}
                          {p.ack_count > 0 && (
                            <span className="text-[9px] text-emerald-600 font-semibold inline-flex items-center gap-0.5" data-testid={`postit-ack-count-${p.id}`} title={p.ack_by.map((a) => a.name).join(", ")}>
                              <ThumbsUp className="h-3 w-3" />{p.ack_count}
                            </span>
                          )}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="postit-dialog">
          <DialogHeader><DialogTitle>{form.id ? t("postit.dialogEdit") : t("postit.dialogNew")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t("postit.message")}</Label>
              <Textarea data-testid="postit-form-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
                placeholder={t("postit.messagePlaceholder")} rows={3} className="mt-1" />
            </div>
            <div>
              <Label>{t("postit.importance")}</Label>
              <div className="flex gap-2 mt-1">
                {ORDER.map((p) => {
                  const m = PRIORITY[p]; const active = form.priority === p;
                  return (
                    <button key={p} type="button" data-testid={`postit-form-priority-${p}`} onClick={() => setForm({ ...form, priority: p })}
                      className={`flex-1 rounded-lg px-3 py-2 text-xs font-bold flex items-center justify-center gap-1.5 transition ring-1 ${active ? `${m.chip} text-white ring-transparent` : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: active ? "#fff" : m.dot }} />{t(`postit.priority.${p}`)}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>{t("postit.recipients")}</Label>
              <select data-testid="postit-form-audience" className="w-full h-10 rounded-md border px-3 bg-background mt-1"
                value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value, roles: [], users: [] })}>
                <option value="all">{t("postit.allTeam")}</option>
                <option value="roles">{t("postit.specificRoles")}</option>
                <option value="users">{t("postit.specificPeople")}</option>
              </select>
            </div>
            {form.audience === "roles" && (
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-1" data-testid="postit-roles">
                {recipients.roles.map((r) => (
                  <label key={r.value} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.roles.includes(r.value)} onCheckedChange={() => toggle("roles", r.value)} data-testid={`postit-role-${r.value}`} />
                    {r.label}
                  </label>
                ))}
              </div>
            )}
            {form.audience === "users" && (
              <div className="grid grid-cols-1 gap-1.5 max-h-40 overflow-y-auto p-1" data-testid="postit-persons">
                {recipients.people.map((u) => (
                  <label key={u.username} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.users.includes(u.username)} onCheckedChange={() => toggle("users", u.username)} data-testid={`postit-person-${u.username}`} />
                    {u.name} <span className="text-xs text-slate-400">· {u.role_label}</span>
                  </label>
                ))}
              </div>
            )}
            {!form.id && (
              <div className="rounded-lg bg-slate-50 ring-1 ring-slate-200 p-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer font-medium text-slate-700">
                  <Checkbox checked={form.repeat} onCheckedChange={(v) => setForm({ ...form, repeat: !!v })} data-testid="postit-repeat" />
                  {t("postit.pinMulti")}
                </label>
                {form.repeat && (
                  <div className="mt-2 flex items-center gap-2 text-sm">
                    <span className="text-slate-500">{t("postit.fromDateTo", { date: form.date })}</span>
                    <input type="date" min={form.date} value={form.until} onChange={(e) => setForm({ ...form, until: e.target.value })}
                      data-testid="postit-until" className="h-9 rounded-md border px-2 bg-white" />
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>{t("postit.cancel")}</Button>
            <Button data-testid="postit-form-save" onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : form.id ? t("postit.save") : t("postit.publish")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
